import { defineStore, storeToRefs } from "pinia";
import { ref, computed, watch } from "vue";
import { useUserStore, FOLLAWED_FEATURES } from "@follaw-fr/stores/user";
import {
  getInstitutionsNames,
  reverseInstitutionsNames,
  type InstitutionKey,
  type InstitutionName,
} from "@/follaw-fr/utils/institutions.utils";
import {
  fetchAmendmentsList as fetchAmendmentListService,
  fetchAmendmentsStats as fetchAmendmentsStatsService,
  fetchOneAmendment as fetchOneAmendmentService,
  fetchSearchAuthors as fetchSearchAuthorsService,
  downloadExcel as downloadExcelService,
} from "@/follaw-fr/services/apiAmendments";
import { useRouteParams, useRouteQuery } from "@vueuse/router";
import { ContentType } from "../components/reusable/constants/content-type.constant";
import type { ReadAmendmentsZoomPayload } from "@/commons/types/api/api.types";
import type {
  AmendmentFilters,
  AmendmentListingItem,
} from "@/commons/types/api/schema.types";
import { useDebounce } from "@vueuse/core";

const initialAmendmentsFilters = {
  size: 20,
  from: 0,
  sort_field: "created_at", //* Sort field
  sort_order: "desc", //* Sort order
  get_filters: true,
};

// Defining store
export const useAmendmentsStore = defineStore("amendments", () => {
  const docRef = useRouteParams("ref");
  const matchedContent = useRouteQuery<ContentType>(
    "matchedContent",
    ContentType.AllMatch
  );
  const userStore = useUserStore();
  const { follawed } = storeToRefs(useUserStore());
  const { sharedFiltersAsPayload } = storeToRefs(userStore);

  const loading = ref(false);
  const loadingExcel = ref(false);
  const isAuthorFilterLoading = ref(false);
  const isFollawedActive = computed(
    () => matchedContent.value === ContentType.Follawed
  );

  // Stats
  const isStatsLoading = ref(false);
  const amendmentsCount = ref<number | null>(null);
  const legidocCount = ref<number | null>(null);
  const actionTypesCount = ref<unknown>({});

  // Listing
  const items = ref<AmendmentListingItem[]>([]);
  const item_count = ref<number | null>(null);

  // Zoom
  const zoom = ref<AmendmentListingItem | null>(null);
  const previousAmendmentId = ref<string | null>(null);
  const nextAmendmentId = ref<string | null>(null);

  // Available filters
  const filters = ref<AmendmentFilters | null>(null);

  // Filters
  const selectedFilters = ref({ ...initialAmendmentsFilters });
  const authors = ref<any>([]);
  const urlFilters = computed(() => ({
    ...(docRef.value ? { doc_ref: [docRef.value] } : {}),
    ...(isFollawedActive.value
      ? { _id: follawed.value[FOLLAWED_FEATURES.AMENDMENTS] }
      : {}),
  }));

  const hasActiveFilters = computed(() => {
    return Object.values(selectedFilters.value).some((filter) => {
      return Array.isArray(filter) && filter.length > 0;
    });
  });

  const amendmentPayload = computed(() => {
    const payload: any = {
      ...urlFilters.value,
      ...selectedFilters.value,
      ...sharedFiltersAsPayload.value,
    };

    if (payload.bypass_time_range) {
      // removes the time filters from sharedFiltersAsPayload
      delete payload.start;
      delete payload.end;
      delete payload.bypass_time_range;
    }

    // Convert back the "institution name" filter if it exists
    if (payload.institution) {
      payload.institution = payload.institution.map(
        (institution: InstitutionName) => reverseInstitutionsNames(institution)
      );
    }

    //* If there is author filters selected map them to only their ids
    if (payload.author?.length > 0) {
      payload.author = payload.author.map((author: any) => author._id);
    }

    //* Remove all empty arrays (filters) from the object to send to the back
    Object.keys(payload).forEach((element) => {
      if (Array.isArray(payload[element]) && payload[element].length === 0) {
        delete payload[element];
      }
    });

    return payload;
  });

  const fetchAmendmentsStats = async () => {
    isStatsLoading.value = true;
    const data = await fetchAmendmentsStatsService(amendmentPayload.value);

    if (data) {
      amendmentsCount.value = data.amendment_count ?? null;
      legidocCount.value = data.legi_doc_count ?? null;
      actionTypesCount.value = data.action_type_count;
    }
    isStatsLoading.value = false;
  };

  const fetchAmendmentsList = async () => {
    loading.value = true;

    const data = await fetchAmendmentListService(amendmentPayload.value);
    if (data) {
      items.value = data.items.map((amendment: AmendmentListingItem) => ({
        ...amendment,
        institution: getInstitutionsNames(
          amendment.institution as InstitutionKey
        ),
      }));
      item_count.value = data.item_count;
      filters.value = data.filters ?? null;
      if (filters.value) {
        filters.value.institution =
          filters.value.institution?.map(getInstitutionsNames);
      }
    }

    loading.value = false;
  };

  const downloadExcel = async () => {
    loadingExcel.value = true;

    const payload = amendmentPayload.value;
    const data = await downloadExcelService(payload);
    if (data) {
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(data);
      link.download = "amendments.xlsx";
      link.click();
    }

    loadingExcel.value = false;
  };

  const fetchOneAmendment = async (amendmentId: string) => {
    loading.value = true;

    const fetchOneAmendmentPayload: ReadAmendmentsZoomPayload = {
      _id: [amendmentId],
      highlight: true,
      ...sharedFiltersAsPayload.value,
    };
    const data = await fetchOneAmendmentService(fetchOneAmendmentPayload);
    if (!data?.items?.length) {
      return null;
    }

    //* set the zoom and get the previous and next ones ID if all went well
    zoom.value = data.items[0];

    //institution formatting
    if (zoom.value) {
      zoom.value.institution = getInstitutionsNames(
        zoom.value.institution as InstitutionKey
      );
    }
    const currentIndex = items.value.findIndex(
      (item) => item._id === zoom.value?._id
    );
    previousAmendmentId.value = items.value[currentIndex - 1]?._id ?? null;
    nextAmendmentId.value = items.value[currentIndex + 1]?._id ?? null;

    loading.value = false;
    return zoom.value;
  };

  const resetSearchAuthorsFilters = () => {
    authors.value = [];
  };

  const getSearchAuthorsFilters = async (nameSearch: string) => {
    isAuthorFilterLoading.value = true;

    authors.value = await fetchSearchAuthorsService(nameSearch);

    isAuthorFilterLoading.value = false;
  };

  const resetFilters = () => {
    selectedFilters.value = { ...initialAmendmentsFilters };
  };

  const resetStore = () => {
    items.value = [];
    selectedFilters.value = { ...initialAmendmentsFilters };
    filters.value = null;
    loading.value = false;

    item_count.value = null;
  };

  return {
    loading,
    loadingExcel,
    isStatsLoading,
    amendmentsCount,
    legidocCount,
    actionTypesCount,
    items,
    item_count,
    zoom,
    previousAmendmentId,
    nextAmendmentId,
    filters,
    selectedFilters,
    isFollawedActive,
    hasActiveFilters,
    authors,
    resetSearchAuthorsFilters,
    downloadExcel,
    fetchOneAmendment,
    resetFilters,
    resetStore,
    getSearchAuthorsFilters,
    isAuthorFilterLoading,
    fetchAmendmentsList,
    fetchAmendmentsStats,
    amendmentPayload,
  };
});

/** Auto refresh amendment stats and list when filter changes */
export const useAutoFetchAmendments = () => {
  const { amendmentPayload } = storeToRefs(useAmendmentsStore());
  const debouncedAmendmentPayload = useDebounce(amendmentPayload, 800);
  const { fetchAmendmentsList, fetchAmendmentsStats } = useAmendmentsStore();
  watch(
    () => debouncedAmendmentPayload,
    async () => {
      await fetchAmendmentsList();
      await fetchAmendmentsStats();
    },
    { deep: true, immediate: true }
  );
};
